import * as React from 'react';
import {ClientSummary, GridRequest, PageResponseEnvelope, Site} from '../api/dtos/dtos';
import furoApi from '../api/FuroApi';
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';
import {OptionProps, SelectValue} from 'antd/lib/select';
import {addIfNotIn, findValueById} from '../utils/utils';
import {Modal, Select} from 'antd';
import {clientIdChanged} from "./ClientContactSelector";
import {normalizeForSearch} from "../utils/string-utils";
import {showSaveSuccessNotification} from "../utils/dialogs";
import ClientSiteForm from "./ClientSiteForm";
import locales from "../locales";
import Label from "../common/Label";
import DropdownRender from "../common/DropdownRender";


interface ClientSiteEditorProps {
  client: ClientSummary | undefined;
  site?: Site;
  onSiteChange: (site?: Site) => void;
  disabled: boolean;
}

interface ClientSiteEditorState {
  siteOptions?: Site[];
  editedSite?: Site;
}

class ClientSiteSelector extends React.Component<ClientSiteEditorProps, ClientSiteEditorState> {

  public state: ClientSiteEditorState = {};
  public readonly loadingHandler?: LoadingOverlayHandler;

  public componentDidUpdate(prevProps: Readonly<ClientSiteEditorProps>, prevState: Readonly<ClientSiteEditorState>) {
    if (clientIdChanged(prevProps, this.props)) {
      this.onClientChanged();
    }
  }

  public render(): React.ReactNode {
    const {site} = this.props;
    const {siteOptions, editedSite} = this.state;

    // Don't set initial values until the options are available
    const siteValue = siteOptions && site && site.id ? site.id : undefined;

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <Select
          showSearch={true}
          allowClear={true}
          placeholder={locales.choose}
          className="mild-disabled"
          disabled={this.isFieldDisabled()}
          value={siteValue}
          onChange={this.onSiteChange}
          filterOption={this.filterOptions}
          dropdownRender={this.renderDropdownOptions}
        >
          {siteOptions && siteOptions.map(this.renderOptions)}
        </Select>

        <Modal
          title={locales.add_site}
          visible={editedSite !== undefined}
          footer={null}
          onCancel={this.onSiteEditFormCancel}
          destroyOnClose={true}
        >
          {editedSite && <ClientSiteForm
              site={editedSite}
              onFormSubmit={this.onSiteEditFormSubmit}
              onFormCancel={this.onSiteEditFormCancel}
          />}
        </Modal>
      </>
    );
  }

  private filterOptions = (input: string, option: React.ReactElement<OptionProps>) => {
    const inp = normalizeForSearch(input);
    const opt = normalizeForSearch(option.props.title || "");
    return opt.indexOf(inp) >= 0;
  };

  private renderOptions = (s: Site): React.ReactNode => {
    return (
      <Select.Option
        value={s.id ? s.id : undefined}
        key={String(s.id)}
        title={s.address}
      >
        {s.name ? <>{s.address}<Label value={`- ${s.name}`}/></> : s.address}
      </Select.Option>
    );
  };

  private renderDropdownOptions = (optionItems: React.ReactNode) => {
    return (
      <DropdownRender options={optionItems} addItem={this.addItem}/>
    )
  };

  private addItem = () => {
    this.setState({
      editedSite: {
        id: null,
        name: null,
        address: '',
      },
    });
  };

  private onSiteEditFormCancel = () => {
    this.setState({editedSite: undefined});
  };

  private onSiteEditFormSubmit = (site: Site) => {
    const clientId = this.props.client!.id!;
    withLoadingOverlay(
      furoApi.clients.createSite(clientId, site)
        .then(this.onSaveContactSuccess)
      , this.loadingHandler);

  };

  private onSaveContactSuccess = () => {
    this.setState({editedSite: undefined});
    this.onClientChanged();
    showSaveSuccessNotification();
  };


  private isFieldDisabled = () => {
    const {client, disabled} = this.props;
    const hasClient = client !== undefined;
    return disabled || !hasClient;
  };

  private onSiteChange = (value: SelectValue) => {
    this.props.onSiteChange(findValueById(this.state.siteOptions, Number(value)));
  };

  private loadSiteOptions = (clientId: number) => {
    const request: GridRequest = {pageSize: 1000, sortColumnKey: 'name', sortOrder: 'ascend'};
    const promise = furoApi.clients.getAllSites(clientId, request)
      .then(this.onSitesLoaded);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onSitesLoaded = (response: PageResponseEnvelope<Site>) => {
    const options = response.results.content;
    this.addCurrentSiteToOptionsIfNotFound(options);
    this.setState({siteOptions: options});
  };

  // If current site is deleted, the backend won't include it in the options response, the dropdown
  // won't show it properly (just the ID/value). So we have to include the current site in the options
  private addCurrentSiteToOptionsIfNotFound(options: Site[]) {
    const {site} = this.props;
    if (site) {
      addIfNotIn(options, site);
    }
  }

  private onClientChanged = () => {
    const client = this.props.client;
    if (client && client.id !== null) {
      this.loadSiteOptions(client.id);
    } else {
      this.clearSiteEditor();
    }
  };

  private clearSiteEditor = () => {
    this.props.onSiteChange(undefined);
    this.setState({
      siteOptions: undefined,
    });
  };

}

export default ClientSiteSelector;



// WEBPACK FOOTER //
// ./src/client-details/ClientSiteSelector.tsx